import { useSyncExternalStore } from 'react';

let snapshot = { ownerKey: '', generation: 0 };
const listeners = new Set();
const requests = new Set();
const mutations = new Set();
let companySwitch = false;
export const SESSION_IDENTITY_EVENT_KEY = 'byggo_company_session_identity_v1';

export const getSessionScope = () => snapshot;
export function subscribeSessionScope(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
export function useSessionScope() {
  return useSyncExternalStore(subscribeSessionScope, getSessionScope, getSessionScope);
}
export function sessionOwnerKey(user) {
  if (user?.id && (user.partner_only || user.role === 'partner')) return `partner:${user.id}`;
  const company = user?.current_company_id || user?.company_id;
  return user?.id && company ? `${company}:${user.id}` : '';
}
export function resetSessionScope(ownerKey = '', broadcast = false) {
  snapshot = { ownerKey, generation: snapshot.generation + 1 };
  for (const controller of requests) controller.abort();
  requests.clear();
  for (const listener of listeners) listener();
  if (broadcast && typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(SESSION_IDENTITY_EVENT_KEY, JSON.stringify({ ownerKey, changedAt: Date.now(), nonce: Math.random() }));
    } catch {
      // The in-memory boundary remains effective when storage is unavailable.
    }
  }
}
export function establishSessionScope(user) {
  const ownerKey = typeof user === 'string' ? user : sessionOwnerKey(user);
  if (ownerKey !== snapshot.ownerKey) resetSessionScope(ownerKey, true);
}
export function beginCompanySwitch() {
  if (companySwitch || mutations.size) throw new Error('Vänta tills pågående ändringar har sparats innan du byter företag.');
  companySwitch = true;
  return () => { companySwitch = false; };
}
export function assertSessionMutationAllowed(path) {
  if (companySwitch && path !== '/auth/switch-company') throw new Error('Ett företagsbyte pågår. Vänta tills det är klart.');
}
export function scopedRequest(signal, mutation = false) {
  const generation = snapshot.generation;
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (signal?.aborted) abort();
  else signal?.addEventListener('abort', abort, { once: true });
  requests.add(controller);
  if (mutation) mutations.add(controller);
  return {
    signal: controller.signal,
    assertCurrent() {
      if (controller.signal.aborted || generation !== snapshot.generation) {
        throw new DOMException('Sessionen har ändrats. Försök igen i det aktuella företaget.', 'AbortError');
      }
    },
    dispose() {
      requests.delete(controller);
      mutations.delete(controller);
      signal?.removeEventListener('abort', abort);
    },
  };
}
